/**
 * Shared react-markdown element to Mantine wrappers.
 *
 * These slices hold ONLY the table and heading wrappers, which are pure,
 * branch-free `{children}` passthroughs and identical across every renderer.
 *
 * hr, p, blockquote, code, pre, a, ul, ol and li are deliberately excluded:
 * each renderer composes its own divergent overrides on top of these slices.
 * The static core renderer uses a native <hr> and overrides block/list/code/link
 * elements; the streaming chat renderer uses a Mantine <Divider> for hr and omits
 * p/ol/ul/li so plain HTML defaults are used during 5-10 Hz message streaming.
 * Adding any of those keys here would silently regress one renderer's contract.
 */

import { Table, Title } from '@mantine/core';
import type { ReactNode } from 'react';

export const markdownTableElements = {
  table({ children }: { children?: ReactNode }) {
    return <Table>{children}</Table>;
  },
  thead({ children }: { children?: ReactNode }) {
    return <Table.Thead>{children}</Table.Thead>;
  },
  tbody({ children }: { children?: ReactNode }) {
    return <Table.Tbody>{children}</Table.Tbody>;
  },
  tr({ children }: { children?: ReactNode }) {
    return <Table.Tr>{children}</Table.Tr>;
  },
  th({ children }: { children?: ReactNode }) {
    return <Table.Th>{children}</Table.Th>;
  },
  td({ children }: { children?: ReactNode }) {
    return <Table.Td>{children}</Table.Td>;
  },
};

export const markdownHeadingElements = {
  h1({ children }: { children?: ReactNode }) {
    return <Title order={1}>{children}</Title>;
  },
  h2({ children }: { children?: ReactNode }) {
    return <Title order={2}>{children}</Title>;
  },
  h3({ children }: { children?: ReactNode }) {
    return <Title order={3}>{children}</Title>;
  },
  h4({ children }: { children?: ReactNode }) {
    return <Title order={4}>{children}</Title>;
  },
  h5({ children }: { children?: ReactNode }) {
    return <Title order={5}>{children}</Title>;
  },
  h6({ children }: { children?: ReactNode }) {
    return <Title order={6}>{children}</Title>;
  },
};
